import React, {useEffect, useState} from 'react';
import ReminderEntryComponent from './ReminderEntryComponent';
import AddReminderComponent from "./AddReminderComponent";
import '../../../css/gardenManagerStylings/plantsSection/GardenManagerRemindersStyling.css';

const GardenManagerRemindersComponent = ({ garden, plants, fetchedReminders, onRefreshReminders }) => {

    const [reminders, setReminders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showActiveReminders, setShowActiveReminders] = useState(true);
    const [showCompleted, setShowCompleted] = useState(false);
    const [showAddEvent, setShowAddEvent] = useState(false);
    const [currentView, setCurrentView] = useState('active');

    // Update reminders whenever fetchedReminders changes
    useEffect(() => {
        if (fetchedReminders && garden?.id) {
            const gardenReminders = fetchedReminders.filter(reminder => reminder.garden_id === garden.id);
            setReminders(gardenReminders);
            setLoading(false);
        }
    }, [fetchedReminders, garden?.id]);

    const toggleReminderComplete = async (reminderId) => {
        try {
            const reminder = reminders.find(r => r.id === reminderId);
            const response = await fetch(`/api/reminder/updateReminder/${reminderId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    is_completed: !reminder.is_completed
                }),
            });

            if (response.ok) {
                // Update local state immediately for better UX
                setReminders(reminders.map(r =>
                    r.id === reminderId
                        ? { ...r, is_completed: !r.is_completed }
                        : r
                ));

                // Also refresh the parent's reminders to keep everything in sync
                onRefreshReminders();
            }
        } catch (error) {
            console.error('Error updating reminder:', error);
        }
    };

    const formatDateTime = (dateTimeString) => {
        const date = new Date(dateTimeString);

        // Check if date is valid
        if (isNaN(date.getTime())) {
            console.error('Invalid date:', dateTimeString);
            return 'Invalid Date';
        }

        const now = new Date();

        // Use local dates for comparison (ignore time)
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const reminderDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        const timeString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        if (reminderDate.getTime() === today.getTime()) {
            return `Today at ${timeString}`;
        } else if (reminderDate.getTime() === yesterday.getTime()) {
            return `Yesterday at ${timeString}`;
        } else if (reminderDate.getTime() === tomorrow.getTime()) {
            return `Tomorrow at ${timeString}`;
        } else {
            return date.toLocaleDateString() + ' at ' + timeString;
        }
    };

    const isOverdue = (dateTimeString) => {
        const date = new Date(dateTimeString);
        const now = new Date();

        // A reminder is overdue if the datetime has passed
        return date < now;
    };

    const getPriorityIcon = (priority) => {
        switch(priority) {
            case 3: return "🔴";
            case 2: return "🟡";
            case 1: return "🟢";
            default: return "⚪";
        }
    };

    const getPlantName = (plantId) => {
        const plant = plants.find(p => p.id === plantId);
        return plant ? plant.plant_name || plant.name : null;
    };

    const handleViewChange = (view) => {
        setCurrentView(view);
        setShowActiveReminders(view === 'active');
        setShowCompleted(view === 'completed');
        setShowAddEvent(view === 'add');
    };

    // Create a callback that also switches back to active view after adding
    const handleReminderAdded = () => {
        onRefreshReminders();
        // Optionally switch back to active view to see the new reminder
        handleViewChange('active');
    };

    const activeReminders = reminders.filter(r => !r.is_completed);
    const completedReminders = reminders.filter(r => r.is_completed);
    const displayReminders = showCompleted ? completedReminders : activeReminders;

    return (
        <div className="garden-manager-reminders-section">
            {/* Header (for selecting active/completed/add event */}
            <div className={`garden-manager-reminders-section-header`}>
                <button
                    className={`garden-manager-reminders-section-header-button ${currentView === 'active' ? 'selected' : ''}`}
                    onClick={() => handleViewChange('active')}
                >
                    Active ({activeReminders.length})
                </button>
                <button
                    className={`garden-manager-reminders-section-header-button ${currentView === 'completed' ? 'selected' : ''}`}
                    onClick={() => handleViewChange('completed')}
                >
                    Completed ({completedReminders.length})
                </button>
                <button
                    className={`garden-manager-reminders-section-header-button ${currentView === 'add' ? 'selected' : ''}`}
                    onClick={() => handleViewChange('add')}
                >
                    Add New
                </button>
            </div>

            {/* Active Reminders Section */}
            {activeReminders && showActiveReminders ? (
                activeReminders.length > 0 ? (
                    <div className="garden-manager-active-reminders-section">
                        {displayReminders.map((reminder) => (
                            <ReminderEntryComponent
                                key={reminder.id}
                                reminder={reminder}
                                gardenName={garden.name}
                                onToggleComplete={toggleReminderComplete}
                                formatDateTime={formatDateTime}
                                isOverdue={isOverdue}
                                getPriorityIcon={getPriorityIcon}
                                getPlantName={getPlantName}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="no-entries-message">
                        <p>No active reminders</p>
                    </div>
                )
            ) : null}

            {/* Completed Reminders Section */}
            {completedReminders && showCompleted ? (
                completedReminders.length > 0 ? (
                    <div className="garden-manager-completed-reminders-section">
                        {completedReminders.map((reminder) => (
                            <ReminderEntryComponent
                                key={reminder.id}
                                reminder={reminder}
                                gardenName={garden.name}
                                onToggleComplete={toggleReminderComplete}
                                formatDateTime={formatDateTime}
                                isOverdue={isOverdue}
                                getPriorityIcon={getPriorityIcon}
                                getPlantName={getPlantName}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="no-entries-message">
                        <p>No completed reminders</p>
                    </div>
                )
            ) : null}

            {/* Section for adding(/editing?) a reminder */}
            {showAddEvent ? (
                <div className={`garden-manager-add-reminders-section`}>
                    <AddReminderComponent
                        garden={garden}
                        reminderRefresh={handleReminderAdded}
                    />
                </div>
            ) : null}
        </div>
    )
}

export default GardenManagerRemindersComponent;